import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Drawer from "@shared/components/ui-kit/overlays/Drawer/Drawer.web";
import { useDisclosure } from "@shared/hooks/useDisclosure";
import useWindowSize from "@shared/hooks/useWindowSize";
import { useTranslation } from "@shared/utils/internationalization";
import breakpoints from "@shared/theme/props/breakpoints";
import { domElementIds } from "@shared/constants/common";
import { waitForElementToLoad } from "@shared/services/loaders/element-loader";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Button from "@ui-kit/form/Button/Button.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Icon from "@ui-kit/icons/Icon.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { MapTimeslotDrawerProps } from "./MapTimeslotDrawer.props";
import MapTimeslotItem from "./MapTimeslotItem/MapTimeslotItem.web";

const { md } = breakpoints;
const { navbarId, navbarBannerId, seatingBannerId } = domElementIds;

const DRAWER_BUTTON_HEIGHT = 50;

/**
 * Drawer that displays a sidebar with timeslot information for recurring assigned seating events.
 * For desktop and larger tablet sizes.
 * Used on public assigned seating map pages.
 */
const MapTimeslotDrawer = ({
  childEvents,
  currentEventId,
}: MapTimeslotDrawerProps) => {
  const { t } = useTranslation("seating");

  const ref = useRef<HTMLDivElement | null>(null);

  const { width: windowWidth } = useWindowSize();
  const isSmallerThanMd = useMemo(
    () => Boolean(windowWidth && windowWidth < md - 1),
    [windowWidth]
  );

  const [drawerMargin, setDrawerMargin] = useState("0px");

  // get the height of any navbar banners that are currently displayed
  // plus the navbar height and seating header height
  // set the drawer's marginTop based on the retrieved height
  const handleDrawerPosition = useCallback(() => {
    const navbarHeight = document?.getElementById(navbarId)?.offsetHeight ?? 0;
    const bannerHeight =
      document?.getElementById(navbarBannerId)?.offsetHeight ?? 0;
    const seatingBannerHeight =
      document?.getElementById(seatingBannerId)?.offsetHeight ?? 0;

    setDrawerMargin(
      `${
        navbarHeight + bannerHeight + seatingBannerHeight + DRAWER_BUTTON_HEIGHT
      }px`
    );
  }, []);

  useEffect(() => {
    waitForElementToLoad(`#${seatingBannerId}`).then(() => {
      handleDrawerPosition();
    });
  }, [handleDrawerPosition]);

  const {
    isOpen: isDrawerOpen,
    onOpen: onDrawerOpen,
    onClose: onDrawerClose,
  } = useDisclosure({});

  // for recurring assigned seating events,
  // it removes the current event from the sidebar
  // so that only the other timeslots are shown
  const getSelectableChildEvents = useMemo(() => {
    return (
      childEvents.filter((childEvent) => childEvent.id !== currentEventId) ?? []
    );
  }, [currentEventId, childEvents]);

  // on page load
  // if the screen size is above md and some child events exist, open the recurring events drawer
  useEffect(() => {
    if (
      !isDrawerOpen &&
      !isSmallerThanMd &&
      getSelectableChildEvents?.length > 0
    ) {
      onDrawerOpen();
    }
    // this useEffect should only run once, so no dependencies
  }, []); // eslint-disable-line

  // Close the drawer when the screen size falls below md
  useEffect(() => {
    if (isDrawerOpen && isSmallerThanMd) {
      onDrawerClose();
    }
  }, [isDrawerOpen, onDrawerClose, isSmallerThanMd]);

  return (
    <>
      {/* Portal used by the Drawer */}
      <Box zIndex="sticky" ref={ref} />
      <Drawer
        variant="interactive"
        trapFocus={false}
        blockScrollOnMount={false} // allows users to scroll on other page components
        placement="left"
        onClose={onDrawerClose}
        isOpen={isDrawerOpen}
        portalProps={{
          appendToParentPortal: true,
          containerRef: ref,
        }}
      >
        <Drawer.Overlay display="none" />
        <Drawer.Content
          borderRightWidth="1px"
          borderRightStyle="solid"
          borderRightColor="border"
          boxShadow="none"
          maxW="element.11xl"
          maxH={`calc(var(--100dvh) - ${drawerMargin})`}
          mt={drawerMargin}
        >
          <Drawer.Body p="none">
            <div data-testid="seating-timeslot-drawer">
              {getSelectableChildEvents?.map((ce) => (
                <MapTimeslotItem
                  key={ce.id}
                  childEvent={ce}
                  containerPadding="8px 24px"
                  titleFontSize="14px"
                  dateFontSize="12px"
                />
              ))}
            </div>
          </Drawer.Body>
        </Drawer.Content>
      </Drawer>
      {/* Hide the drawer button if the screen is smaller than tablet or there is only one timeslot */}
      <Flex
        data-testid="seating-timeslot-drawer-button"
        display="flex"
        direction="column"
        boxShadow="none"
        borderRight="1px solid"
        borderBottom="1px solid"
        borderColor="gray.400"
        backgroundColor="bg"
        minWidth="fit-content"
        maxWidth="element.11xl"
        height="auto"
        position="absolute"
        zIndex="sticky"
        hidden={isSmallerThanMd || getSelectableChildEvents?.length === 0}
      >
        <Button
          data-testid="map-timeslot-drawer-toggle"
          variant="link"
          boxShadow="none"
          onClick={isDrawerOpen ? onDrawerClose : onDrawerOpen}
        >
          <Flex
            h={`${DRAWER_BUTTON_HEIGHT}px`}
            p="0px 24px"
            justifyContent="space-between"
            alignItems="center"
            fontWeight="bold"
            color="font.dark"
            fontSize="sm"
            w="element.11xl"
          >
            <Text>{t("More Events")}</Text>
            {isDrawerOpen ? (
              <Icon.ChevronDown boxSize="element.md" />
            ) : (
              <Icon.ChevronRight boxSize="element.md" />
            )}
          </Flex>
        </Button>
      </Flex>
    </>
  );
};

export default MapTimeslotDrawer;
